import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useLocation } from "react-router-dom";
import "../styles/crick.css";
import endpoints from "../api/endpoints";
import STRINGS from "../globals/strings";
import IMAGES from "../globals/images";
import Summary from "../components/summary";
import Scorecard from "../components/scorecard";
import PopUp from "../components/popUp";
import Meta from "./Meta";
import LoadingSpinner from "../components/LoadingSpinner";

const Crick = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const { matchId, matchType } = useParams();
	const [activeTab, setActiveTab] = useState("Scorecard");
	const [selectedTeam, setSelectedTeam] = useState(null);
	const [activeLink, setActiveLink] = useState("Betting");
	const [isPopupOpen, setIsPopupOpen] = useState(false);
	const [selectedBowler, setSelectedBowler] = useState(null);
	const [matchData, setMatchData] = useState(null);
	const [summaryData, setSummaryData] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);

	useEffect(() => {
		let summaryData;

		const fetchFinalScoreCard = async () => {
			try {
				setLoading(true);
				const data = await endpoints.fetchFinalScoreCard(matchId, matchType);
				// console.log(`FinalScorecard API Response for match_type: ${matchType}:`, data);

				if (data.success && data.data) {
					setSummaryData(data.data);
					summaryData = data.data;
				} else {
					console.error(`Invalid or empty finalScoreCard API data for match_type: ${matchType}:`, data);
					setSummaryData(null);
				}
			} catch (err) {
				console.error(`finalScoreCard API Error for match_type: ${matchType}:`, err.message);
				setSummaryData(null);
			} finally {
				setLoading(false);
			}
		};

		const fetchScorecard = async () => {
			if (!matchId || !matchType) {
				setError(STRINGS.INVALID_MATCH_ID_TYPE);
				setMatchData(null);
				return;
			}

			try {
				setLoading(true);
				setError(null);
				const data = await endpoints.fetchScorecard(matchId, matchType);
				// console.log(`Scorecard API Response for match_type: ${matchType}:`, data);

				if (data.success && data.data && Array.isArray(data.data) && data.data.length > 0) {
					const normalizedData = normalizeData(data.data);
					// console.log('Normalized Data:', normalizedData);
					setMatchData(normalizedData);
					setSelectedTeam(normalizedData?.team1?.team_name || "Team 1");
				} else {
					console.error(`Invalid or empty Scorecard API data for match_type: ${matchType}:`, data);
					setError(STRINGS.NO_SCORECARD_DATA);
					setMatchData(null);
				}
			} catch (err) {
				console.error(`Scorecard API Error for match_type: ${matchType}:`, err.message);
				setError(`${STRINGS.FAILED_TO_FETCH_SCORECARD}${err.message}`);
				setMatchData(null);
			} finally {
				setLoading(false);
			}
		};

		const normalizeData = (apiData) => {
			if (!apiData || !Array.isArray(apiData) || apiData.length < 1) {
				console.log('Invalid API data:', apiData);
				return null;
			}

			const team1 = apiData[0] || {};
			const team2 = apiData[1] || {};
			const matchInfo = apiData[2] || {};   // ← add this

			return {
				team1: {
					batting_team: team1.batting_team_id || "",
					bowling_team: team1.bowling_team_id || "",
					team_name: team1.team_name || "Team 1",
					team_image: team1.team_image || team1.image || IMAGES.RED_LIONS_LOGO,
					team_id: team1.team_id || "",
					inning_total: {
						total_runs: team1.inning_total?.total_runs || 0,
						total_wickets: team1.inning_total?.total_wickets || 0,
						overs: team1.inning_total?.overs || 0,
						match_overs: team1.inning_total?.match_overs || 20,
						extra: {
							total: team1.inning_total?.extra?.total || 0,
							bye: team1.inning_total?.extra?.bye || 0,
							leg_bye: team1.inning_total?.extra?.leg_bye || 0,
							wide_ball: team1.inning_total?.extra?.wide_ball || 0,
							no_ball: team1.inning_total?.extra?.no_ball || 0,
						},
						new_target: team1.inning_total?.new_target || 0,
						current_run_rate: team1.inning_total?.current_run_rate || 0,
						is_new_target: team1.inning_total?.is_new_target || 0,
						new_target_info: team1.inning_total?.new_target_info || "",
					},
					batting: team1.batting?.map((b) => ({
						batsman_name: b.name || "Unknown",
						total_runs: b.total_runs || 0,
						total_balls: b.total_balls || 0,
						total_fours: b.total_fours || 0,
						total_sixes: b.total_sixes || 0,
						strike_rate: b.strike_rate || 0,
						is_not_out: b.is_not_out || 0,
						out_information: b.out_information || null,
						created_at: b.created_at || null,
					})) || [],
					bowling: team1.bowling?.map((b) => ({
						bowler_name: b.bowler_first_name && b.bowler_last_name
							? `${b.bowler_first_name} ${b.bowler_last_name}`.trim()
							: `${b.bowler_first_name || "Unknown"}`.trim(),
						total_over: b.total_over || 0,
						total_maiden_over: b.total_maiden_over || 0,
						total_run: b.total_run || 0,
						total_wicket: b.total_wicket || 0,
						economy: b.economy || 0,
						bowler_id: b.bowler_id || "",
						team_name: team1.team_name || "Team 1",
					})) || [],
					fall_of_wicket: team1.fall_of_wicket || [],
					partnership: team1.partnership?.map((p) => ({
						batsman_1_name: p.batsman_1_name || "Unknown",
						batsman_1_total_run: p.batsman_1_total_run || 0,
						batsman_1_total_ball: p.batsman_1_total_ball || 0,
						batsman_2_name: p.batsman_2_name || "Unknown",
						batsman_2_total_run: p.batsman_2_total_run || 0,
						batsman_2_total_ball: p.batsman_2_total_ball || 0,
						extra: p.extra || 0,
						score: p.score || 0,
						ball: p.ball || 0,
					})) || [],
					over_history: team1.over_history?.map((o) => ({
						batsmans_list: o.batsmans_list || "",
						over_number: o.over_number || 0,
						bowler_name: o.bowler_name || "Unknown",
						ball_history: o.ball_history || [],
						total_run_from_over: o.total_run_from_over || 0,
					})) || [],
				},
				team2: {
					batting_team: team2.batting_team_id || "",
					bowling_team: team2.bowling_team_id || "",
					team_name: team2.team_name || "Team 2",
					team_image: team2.team_image || team2.image || IMAGES.RED_LIONS_LOGO,
					team_id: team2.team_id || "",
					inning_total: {
						total_runs: team2.inning_total?.total_runs || 0,
						total_wickets: team2.inning_total?.total_wickets || 0,
						overs: team2.inning_total?.overs || 0,
						match_overs: team2.inning_total?.match_overs || 20,
						extra: {
							total: team2.inning_total?.extra?.total || 0,
							bye: team2.inning_total?.extra?.bye || 0,
							leg_bye: team2.inning_total?.extra?.leg_bye || 0,
							wide_ball: team2.inning_total?.extra?.wide_ball || 0,
							no_ball: team2.inning_total?.extra?.no_ball || 0,
						},
						new_target: team2.inning_total?.new_target || 0,
						current_run_rate: team2.inning_total?.current_run_rate || 0,
						is_new_target: team2.inning_total?.is_new_target || 0,
						new_target_info: team2.inning_total?.new_target_info || "",
					},
					batting: team2.batting?.map((b) => ({
						batsman_name: b.name || "Unknown",
						total_runs: b.total_runs || 0,
						total_balls: b.total_balls || 0,
						total_fours: b.total_fours || 0,
						total_sixes: b.total_sixes || 0,
						strike_rate: b.strike_rate || 0,
						is_not_out: b.is_not_out || 0,
						out_information: b.out_information || null,
						created_at: b.created_at || null,
					})) || [],
					bowling: team2.bowling?.map((b) => ({
						bowler_name: b.bowler_first_name && b.bowler_last_name
							? `${b.bowler_first_name} ${b.bowler_last_name}`.trim()
							: `${b.bowler_first_name || "Unknown"}`.trim(),
						total_over: b.total_over || 0,
						total_maiden_over: b.total_maiden_over || 0,
						total_run: b.total_run || 0,
						total_wicket: b.total_wicket || 0,
						economy: b.economy || 0,
						bowler_id: b.bowler_id || "",
						team_name: team2.team_name || "Team 2",
					})) || [],
					fall_of_wicket: team2.fall_of_wicket || [],
					partnership: team2.partnership?.map((p) => ({
						batsman_1_name: p.batsman_1_name || "Unknown",
						batsman_1_total_run: p.batsman_1_total_run || 0,
						batsman_1_total_ball: p.batsman_1_total_ball || 0,
						batsman_2_name: p.batsman_2_name || "Unknown",
						batsman_2_total_run: p.batsman_2_total_run || 0,
						batsman_2_total_ball: p.batsman_2_total_ball || 0,
						extra: p.extra || 0,
						score: p.score || 0,
						ball: p.ball || 0,
					})) || [],
					over_history: team2.over_history?.map((o) => ({
						batsmans_list: o.batsmans_list || "",
						over_number: o.over_number || 0,
						bowler_name: o.bowler_name || "Unknown",
						ball_history: o.ball_history || [],
						total_run_from_over: o.total_run_from_over || 0,
					})) || [],
					result: team2.result || null,
				},
				matchSettings: {                          // ← add this block
					is_no_ball_allowed: matchInfo.is_no_ball_allowed ?? 0,
					is_wide_ball_allowed: matchInfo.is_wide_ball_allowed ?? 0,
				},
				playerOfMatch: {
					name: summaryData?.player_of_match?.name || "Unknown",
					team: summaryData?.player_of_match?.team_name || "Team 1",
					image: summaryData?.player_of_match?.profile_picture || IMAGES.KUNAL,
				},
				best_batter: team1.batting?.[0] || team2.batting?.[0] || null,
				best_bowler: team1.bowling?.[0] || team2.bowling?.[0] || null,
			};
		};

		fetchScorecard();
		fetchFinalScoreCard();
	}, [matchId, matchType]);

	// old condition for out info
	// const formatOutInfo = (batterInfo) => {
	// 	let outInfo = "";
	// 	if (batterInfo?.is_not_out === 1) {
	// 		outInfo = "not out";
	// 	} else if (batterInfo?.out_information) {
	// 		const obj = batterInfo.out_information;
	// 		if (obj.out_type === "CatchOut") {
	// 			if (obj.ball_by === obj.out_by) {
	// 				outInfo = `c & b ${obj.ball_by || ""}`;
	// 			} else {
	// 				outInfo = `c ${obj.out_by || ""} b ${obj.ball_by || ""}`;
	// 			}
	// 		} else if (obj.out_type === "BowledOut") {
	// 			outInfo = `b ${obj.ball_by || ""}`;
	// 		} else if (obj.out_type === "StOut") {
	// 			outInfo = `st ${obj.out_by || ""}`;
	// 		} else if (obj.out_type === "RunOut") {
	// 			let outText = "";
	// 			if (obj.out_by && obj.out_by !== "") {
	// 				outText = `run out (${obj.out_by})`;
	// 			}
	// 			if (obj.out_by_2 && obj.out_by_2 !== "") {
	// 				if (outText === "") {
	// 					outText = `run out (${obj.out_by_2})`;
	// 				} else {
	// 					outText = `Run Out (${obj.out_by} - ${obj.out_by_2})`;
	// 				}
	// 			}
	// 			outInfo = outText || "run out";
	// 		} else if (obj.out_type === "HitWicket") {
	// 			outInfo = `hit wicket b ${obj.ball_by || ""}`;
	// 		} else if (obj.out_type === "RetiredOut") {
	// 			outInfo = `RetiredHurt`;
	// 		}
	// 	}
	// 	return outInfo;
	// };

	// new condition for out info
	const formatOutInfo = (batterInfo) => {
		let outInfo = "";

		// If batter is not out
		if (batterInfo?.is_not_out === 1) {
			return "not out";
		}

		// If out information exists
		if (batterInfo?.out_information) {
			const info = batterInfo.out_information;

			switch (info.out_type) {
				case "CatchOut":
					if (info.ball_by === info.out_by) {
						outInfo = `c & b ${info.ball_by || ""}`;
					} else {
						outInfo = `c ${info.out_by || ""} b ${info.ball_by || ""}`;
					}
					break;

				case "BowledOut":
					outInfo = `b ${info.ball_by || ""}`;
					break;

				case "StOut":
					outInfo = `st ${info.out_by || ""}`;
					break;

				case "RunOut": {
					let outText = "";

					if (info.out_by) {
						outText = `run out (${info.out_by})`;
					}

					if (info.out_by_2) {
						outText = outText
							? `Run Out (${info.out_by} - ${info.out_by_2})`
							: `run out (${info.out_by_2})`;
					}

					outInfo = outText || "run out";
					break;
				}

				case "HitWicket":
					outInfo = `hit wicket b ${info.ball_by || ""}`;
					break;

				case "RetiredOut":
					outInfo = `RetiredHurt`;
					break;

				case "LBWOut":
					outInfo = `lbw b ${info.ball_by || ""}`;
					break;

				default:
					outInfo = "";
			}
		}

		return outInfo;
	};


	const handleTeamSelect = (team) => {
		setSelectedTeam(team);
	};

	const handleLinkClick = (link, e) => {
		e.preventDefault();
		setActiveLink(link);
	};

	const pathParts = location.pathname.split("/");
	const routeMatchType = pathParts[3];

	const isQuickMatch = routeMatchType === "qm";

	const googlePlayUrl = isQuickMatch
		? STRINGS.QUICK_APP_GOOGLE_PLAY_URL_RIGHT
		: STRINGS.GOOGLE_PLAY_URL_RIGHT;

	const appStoreUrl = isQuickMatch
		? STRINGS.QUICK_APP_APP_STORE_URL_RIGHT
		: STRINGS.APP_STORE_URL_RIGHT;

	const imageClick = (url) => {
		const newWindow = window.open(url, "_blank", "noopener,noreferrer");
		if (newWindow) newWindow.opener = null;
	};

	const openPopup = (bowler, isSummary = 0, team_name = null) => {
		const bowlerData = {
			bowler_name: bowler.bowler_name || STRINGS.UNKNOWN_BOWLER,
			bowler_id: bowler.bowler_id || "",
			team_name: isSummary === 1 ? team_name : bowler.team_name || "",
		};
		console.log("Opening PopUp with bowler:", bowlerData);
		setSelectedBowler(bowlerData);
		setIsPopupOpen(true);
	};

	const closePopup = () => {
		setIsPopupOpen(false);
		setSelectedBowler(null);
	};

	const getTeamImageUrl = (team) => {
		// if (!team) {
		// 	console.log("Team is undefined or null:", team);
		// 	return IMAGES.PHOENIX_LOGO;
		// }

		const teamImage = team.team_image || team.image;
		const teamName = team.team_name;
		const teamId = team.team_id;

		// console.log('Team Data:', { teamImage, teamName, teamId });

		if (teamImage && teamImage.charAt(teamImage.length - 1) === '/') {
			if (teamId) {
				const url = `${teamImage}${teamId}.png`;
				console.log('Constructed URL with team_id:', url);
				return url;
			} else if (teamName) {
				const formattedName = teamName.replace(/\s+/g, '_').toLowerCase();
				const url = `${teamImage}${formattedName}.png`;
				console.log('Constructed URL with team_name:', url);
				return url;
			} else {
				console.log('Falling back to default image (no team_id or team_name:', IMAGES.PHOENIX_LOGO);
				return IMAGES.PHOENIX_LOGO;
			}
		}

		const url = teamImage || IMAGES.PHOENIX_LOGO;
		// console.log('Using teamImage or default:', url);
		return url;
	};

	const formatDate = (dateString) => {
		if (!dateString) return null;
		try {
			const date = new Date(dateString);
			const options = { year: 'numeric', month: 'short', day: 'numeric' };
			return `${date.toLocaleDateString('en-US', options)}`;
		} catch (e) {
			console.error("Invalid date format:", dateString);
			return null;
		}
	};

	if (loading) {
		return <LoadingSpinner />;
	}

	if (error || !matchData) {
		return (
			<div className="app-container">
				<div className="no-data">
					<p>{error || STRINGS.NO_DATA_AVAILABLE}</p>
				</div>
			</div>
		);
	}

	return (
		<div className="app-container">
			<Meta />
			<div className="header">
				<img
					src={IMAGES.BACKARROW}
					className="back-arrow"
					alt={STRINGS.BACK_ARROW}
					onClick={() => navigate("/")}
				/>
				<img src={IMAGES.LOGO} alt={STRINGS.CRICKSTAR_LOGO_ALT} className="logo" />
			</div>

			<div className="main-content">
				<div className="left-banner">
					<div className="banner-content">
						<img src={IMAGES.LEFT_BANNER} alt={STRINGS.LEFT_BANNER_ALT} className="banner-logo" />
						<div className="left-buttons" style={{ cursor: "pointer" }}>
							<img
								src={IMAGES.GOOGLE_PLAY}
								alt={STRINGS.GOOGLE_PLAY_ALT}
								className="left-button"
								onClick={() => imageClick(STRINGS.GOOGLE_PLAY_URL_LEFT)}
							/>
							<img
								src={IMAGES.APP_STORE}
								alt={STRINGS.APP_STORE_ALT}
								className="left-button"
								onClick={() => imageClick(STRINGS.APP_STORE_URL_LEFT)}
							/>
						</div>
					</div>
				</div>

				<div className="middle-section">
					<div className="tabs">
						<div
							className="transporter"
							style={{ transform: `translateX(${activeTab === "Summary" ? 0 : 100}%)` }}
						></div>
						<button
							className={`tab ${activeTab === "Summary" ? "active" : ""}`}
							onClick={() => setActiveTab("Summary")}
						>
							{STRINGS.SUMMARY_TAB}
						</button>
						<button
							className={`tab ${activeTab === "Scorecard" ? "active" : ""}`}
							onClick={() => setActiveTab("Scorecard")}
						>
							{STRINGS.SCORECARD_TAB}
						</button>
					</div>

					<div
						className={`team-row ${activeTab === "Summary" ? "hide-team" : ""}`}
						style={{ cursor: activeTab === "Summary" ? "default" : "pointer" }}
					>
						<div
							className={`team ${matchData.team2.result &&
								matchData.team2.result.winning_team === matchData.team1.team_name &&
								activeTab === "Summary"
								? "gif-bg"
								: ""
								} ${selectedTeam === matchData.team1.team_name && activeTab !== "Summary" ? "selected-team" : ""}`}
							onClick={() => handleTeamSelect(matchData.team1.team_name)}
							style={{
								borderRadius: "20px 0 0 20px",
								pointerEvents: activeTab === "Summary" ? "none" : "all",
								cursor: activeTab === "Summary" ? "default" : "pointer",
							}}
						>
							<img
								src={getTeamImageUrl(matchData?.team1)}
								onError={(e) => {
									console.log('Image failed to load for team1:', e.target.src);
									e.target.src = IMAGES.PHOENIX_LOGO;
								}}
								alt={STRINGS.TEAM1_LOGO_ALT}
								className="team-logo"
							/>
							<div
								className="team-content"
								style={{
									cursor: activeTab === "Summary" ? "default" : "pointer",
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
								}}
							>
								<div>
									<label
										className="team-name"
										style={{ cursor: activeTab === "Summary" ? "default" : "pointer" }}
									>
										{matchData.team1?.team_name || "Team 1 Not Available"}
									</label>
									<span style={{ display: "flex", alignItems: "center", gap: "15px" }}>
										<label
											className="team-score"
											style={{
												cursor: activeTab === "Summary" ? "default" : "pointer",
												color:
													matchData.team2.result &&
														matchData.team2.result.winning_team === matchData.team1.team_name &&
														activeTab === "Summary"
														? "#3747DE"
														: "",
											}}
										>
											{matchData.team1?.inning_total
												? `${matchData.team1.inning_total.total_runs}-${matchData.team1.inning_total.total_wickets}`
												: "N/A"}
										</label>
										<label
											className="team-over"
											style={{ cursor: activeTab === "Summary" ? "default" : "pointer" }}
										>
											{matchData.team1?.inning_total
												? `(${matchData.team1.inning_total.overs}/${matchData.team1.inning_total.match_overs})`
												: "N/A"}
										</label>
									</span>
								</div>
								<div className="dropdown-area">
									{activeTab === "Scorecard" && (
										<button
											className={`dropdown-button ${selectedTeam === matchData.team1.team_name ? "active" : ""
												}`}
											onClick={() => handleTeamSelect(matchData.team1.team_name)}
										>
											<img
												src={
													selectedTeam === matchData.team1.team_name
														? IMAGES.VECTOR_UP
														: IMAGES.VECTOR_DOWN
												}
												alt={STRINGS.DROPDOWN_ALT}
											/>
										</button>
									)}
								</div>
							</div>
						</div>
						<div
							className={`team ${matchData.team2.result &&
								matchData.team2.result.winning_team === matchData.team2.team_name &&
								activeTab === "Summary"
								? "gif-bg"
								: ""
								} ${selectedTeam === matchData.team2.team_name && activeTab !== "Summary" ? "selected-team" : ""}`}
							onClick={() => handleTeamSelect(matchData.team2.team_name)}
							style={{
								textAlign: "right",
								borderRadius: "0 20px 20px 0",
								pointerEvents: activeTab === "Summary" ? "none" : "all",
							}}
						>
							<img
								src={getTeamImageUrl(matchData?.team2)}
								onError={(e) => {
									console.log('Image failed to load for team2:', e.target.src);
									e.target.src = IMAGES.PHOENIX_LOGO;
								}}
								alt={STRINGS.TEAM2_LOGO_ALT}
								className="team-logo"
							/>
							<div
								className="team-content"
								style={{
									cursor: activeTab === "Summary" ? "default" : "pointer",
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
								}}
							>
								<div className="dropdown-area">
									{activeTab === "Scorecard" && (
										<button
											className={`dropdown-button ${selectedTeam === matchData.team2.team_name ? "active" : ""
												}`}
											onClick={() => handleTeamSelect(matchData.team2.team_name)}
										>
											<img
												src={
													selectedTeam === matchData.team2.team_name
														? IMAGES.VECTOR_UP
														: IMAGES.VECTOR_DOWN
												}
												alt={STRINGS.DROPDOWN_ALT}
											/>
										</button>
									)}
								</div>
								<div>
									<label
										className="team-name"
										style={{ cursor: activeTab === "Summary" ? "default" : "pointer", justifyContent: "flex-end" }}
									>
										{matchData.team2?.team_name || "Team 2 Not Available"}
									</label>
									<span style={{ display: "flex", alignItems: "center", gap: "15px" }}>
										<label
											className="team-over"
											style={{ cursor: activeTab === "Summary" ? "default" : "pointer" }}
										>
											{matchData.team2?.inning_total
												? `(${matchData.team2.inning_total.overs}/${matchData.team2.inning_total.match_overs})`
												: "Yet to bat..."}
										</label>
										<label
											className="team-score"
											style={{
												cursor: activeTab === "Summary" ? "default" : "pointer",
												color:
													matchData.team2.result &&
														matchData.team2.result.winning_team === matchData.team2.team_name &&
														activeTab === "Summary"
														? "#3747DE"
														: "",
											}}
										>
											{matchData.team2?.inning_total && matchData.team2.inning_total.overs > 0
												? `${matchData.team2.inning_total.total_runs}-${matchData.team2.inning_total.total_wickets}`
												: "N/A"}
										</label>
									</span>
								</div>
							</div>
						</div>
						<div className="time-date">
							<label className="date-label">
								{matchData?.team1?.batting?.[0]?.created_at &&
									formatDate(matchData.team1.batting[0].created_at)}
							</label>
						</div>
					</div>

					{matchData.team1.inning_total.is_new_target === 1 && (
						<div className="alibag">
							<div className="target">
								<label className="target-label">
									<span
										dangerouslySetInnerHTML={{
											__html: STRINGS.TARGET_LABEL(
												matchData.team1.team_name,
												matchData.team1.inning_total.total_runs,
												matchData.team2.team_name,
												matchData.team1.inning_total.new_target
											),
										}}
									/>
								</label>
							</div>
						</div>
					)}

					{matchData.team2.result && (
						<div className="jubeli-bag">
							<div className="won-match">
								<p className="match-result">
									{matchData.team2.result.win_by === "Match is Tie"
										? STRINGS.TIE_RESULT(matchData.team2.result.win_by)
										: STRINGS.MATCH_RESULT(matchData.team2.result.winning_team, matchData.team2.result.win_by)}
								</p>
							</div>
						</div>
					)}

					{activeTab === "Summary" && (
						<Summary summaryData={summaryData} matchData={matchData} openPopup={openPopup} />
					)}

					{activeTab === "Scorecard" && (
						<Scorecard
							matchData={matchData}
							selectedTeam={selectedTeam}
							activeLink={activeLink}
							handleLinkClick={handleLinkClick}
							formatOutInfo={formatOutInfo}
							openPopup={openPopup}
							matchSettings={matchData.matchSettings}
						/>
					)}
				</div>

				<div className="right-ad-section">
					<div className="right-ad">
						<img src={IMAGES.RIGHT_BANNER} alt={STRINGS.RIGHT_BANNER_ALT} className="right-banner-image" />
						<div className="ad-content">
							<div className="ad-text-group">
								<h2 className="ad-title">{STRINGS.AD_TITLE}</h2>
								<p className="ad-subtitle">{STRINGS.AD_SUBTITLE}</p>
							</div>
							<div className="store-buttons" style={{ cursor: "pointer" }}>
								<img
									src={IMAGES.GOOGLE_PLAY}
									alt={STRINGS.GOOGLE_PLAY_ALT}
									className="store-button"
									onClick={() => imageClick(googlePlayUrl)}
								/>
								<img
									src={IMAGES.APP_STORE}
									alt={STRINGS.APP_STORE_ALT}
									className="store-button"
									onClick={() => imageClick(appStoreUrl)}
								/>
							</div>
						</div>
					</div>
					<div className="bottom-banner">
						<img src={IMAGES.BOTTOM_BANNER} alt={STRINGS.BOTTOM_BANNER_ALT} className="bottom-banner-image" />
						<div className="bottom-buttons" style={{ cursor: "pointer" }}>
							<img
								src={IMAGES.GOOGLE_PLAY}
								alt={STRINGS.GOOGLE_PLAY_ALT}
								className="bottom-button"
								onClick={() => imageClick(STRINGS.GOOGLE_PLAY_URL_BOTTOM)}
							/>
							<img
								src={IMAGES.APP_STORE}
								alt={STRINGS.APP_STORE_ALT}
								className="bottom-button"
								onClick={() => imageClick(STRINGS.APP_STORE_URL_BOTTOM)}
							/>
						</div>
					</div>
				</div>
			</div>

			{isPopupOpen && (
				<div className="modal-overlay" onClick={closePopup}>
					<div className="modal-content" onClick={(e) => e.stopPropagation()}>
						<PopUp
							bowler={selectedBowler}
							onClose={closePopup}
							matchId={matchId}
							matchType={matchType}
						/>
					</div>
				</div>
			)}
		</div>
	);
};

export default Crick;